/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
import classNames from 'classnames';
import {
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { MaterialIcon } from '../MaterialIcon';

/**
 * Horizontal-scroll affordance state for an overflow-x container. Recomputes on
 * mount, on container resize (ResizeObserver), and whenever `recomputeKey`
 * changes — content width can change without the container's own box resizing
 * (e.g. more/fewer columns), which ResizeObserver alone wouldn't catch.
 */
const useScrollArrows = (recomputeKey?: unknown) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [arrows, setArrows] = useState({ left: false, right: false });

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setArrows((prev) =>
      prev.left === left && prev.right === right ? prev : { left, right },
    );
  }, []);

  useEffect(() => {
    onScroll();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(onScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onScroll, recomputeKey]);

  const scrollByPage = useCallback((dir: -1 | 1) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  }, []);

  return { scrollRef, arrows, onScroll, scrollByPage };
};

// A 30px white circle half-overhanging the edge (mirrors ComparisonTable's
// TableRuleButton), rendered only when there is room to scroll that way.
const ArrowButton = ({
  type,
  show,
  label,
  onClick,
}: {
  type: 'left' | 'right';
  show: boolean;
  label: string;
  onClick: () => void;
}) =>
  show ? (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={classNames(
        'absolute top-1/2 z-[45] flex size-[30px] -translate-y-1/2 items-center justify-center rounded-full bg-fill/card shadow-md',
        type === 'right' ? 'right-[-15px]' : 'left-[-15px]',
      )}
    >
      <MaterialIcon
        icon={type === 'right' ? 'arrow_right_alt' : 'arrow_left_alt'}
        px={18}
        className="text-text/primary"
      />
    </button>
  ) : null;

type ScrollableWithArrowsProps = {
  children: ReactNode;
  /** Classes for the inner scroll container (e.g. overflow-x-auto + card styles). */
  className?: string;
  /** Re-check arrow visibility when this changes (e.g. item count). */
  recomputeKey?: unknown;
  prevLabel?: string;
  nextLabel?: string;
} & Pick<HTMLAttributes<HTMLDivElement>, 'role' | 'aria-label'>;

/**
 * Wraps horizontally-scrollable content and overlays left/right scroll arrows
 * that appear only when the content overflows in that direction.
 */
export const ScrollableWithArrows = ({
  children,
  className,
  recomputeKey,
  prevLabel = 'Scroll left',
  nextLabel = 'Scroll right',
  ...rest
}: ScrollableWithArrowsProps) => {
  const { scrollRef, arrows, onScroll, scrollByPage } =
    useScrollArrows(recomputeKey);

  return (
    <div className="relative">
      <ArrowButton
        type="left"
        show={arrows.left}
        label={prevLabel}
        onClick={() => scrollByPage(-1)}
      />
      <ArrowButton
        type="right"
        show={arrows.right}
        label={nextLabel}
        onClick={() => scrollByPage(1)}
      />
      <div ref={scrollRef} onScroll={onScroll} className={className} {...rest}>
        {children}
      </div>
    </div>
  );
};
